import { formatDateWithoutTimezone, loadGuarantorsFromParticipants, loadFees } from './operationHelpers';
import { mapReceivablesFromSchema } from './dataMappers';
import { formatISOToDate } from './formatters';
import { EMPTY_BILLING_CONFIG } from '../components/BillingConfigSection';
import { ContractApiResponse, ParticipantResponse } from '../types/api-responses';
import { AcknowledgementDebtForm, StructuredOperationForm } from '../types/operations';

const isoToDateBR = (value: string | undefined | null): string => {
    if (!value) return '';
    return value.split('T')[0].split('-').reverse().join('/');
};

/**
 * Maps API response to OperationLoanFormPage form data (CPRFFormData).
 */
export const mapLoanApiResponseToForm = (data: ContractApiResponse) => {
    const schema = data.operation_dictionary || {};
    return {
        type: schema.type || 'CPRF',
        operation_number: data.operation_number || '',
        sequencial_number: data.sequencial_number || 0,
        nominal_value: data.nominal_value || 0,
        loan_amount: data.loan_amount || 0,
        approved_value: data.approved_value?.toString() || '',
        interest_rate: '',
        expiration_date: data.expiration_date ? formatISOToDate(data.expiration_date) : '',
        emission_date: data.emission_date ? formatISOToDate(data.emission_date) : '',
        approved_on: data.approved_on ? formatISOToDate(data.approved_on) : '',
        external_link: schema.external_link || '',
        credit_product_type_name: data.credit_product_type_name || schema.credit_product_type_name || '',
        receiving_account_name: data.receiving_account_name || schema.receiving_account_name || '',
        operation_uf: schema.operation_uf || '',
        operation_city: schema.operation_city || '',
        operation_dictionary: {
            type: schema.type || 'CPRF',
            nominal_value: schema.nominal_value || 0,
            requested_value: schema.requested_value || 0,
            interest_rate: schema.interest_rate || 0,
            emission_date: schema.emission_date ? formatISOToDate(schema.emission_date) : '',
            status: schema.status || 1,
            guarantee_value: schema.guarantee_value || null,
            measures: {
                referency: schema.measures?.referency || 0,
                aggregation_unit_measure: schema.measures?.aggregation_unit_measure || '',
                amount_in_aggregation_unit: schema.measures?.amount_in_aggregation_unit || 0,
                unit_measure: schema.measures?.unit_measure || ''
            },
            commodity: {
                harvest_year_reference: schema.commodity?.harvest_year_reference || '',
                price: schema.commodity?.price || 0,
                quality: schema.commodity?.quality || '',
                description: schema.commodity?.description || '',
                amount: schema.commodity?.amount || 0,
                quotation: {
                    date: schema.commodity?.quotation?.date ? formatISOToDate(schema.commodity.quotation.date) : '',
                    source: schema.commodity?.quotation?.source || '',
                    city: schema.commodity?.quotation?.city || '',
                    province_name: schema.commodity?.quotation?.province_name || '',
                    province_initials: schema.commodity?.quotation?.province_initials || ''
                }
            },
            crop_type: {
                id: schema.crop_type?.id || 0,
                name: schema.crop_type?.name || ''
            },
            main_risk: (() => {
                const mr = schema.main_risk as any;
                if (mr && typeof mr === 'object' && mr.uuid) return { uuid: mr.uuid, name: mr.name || '' };
                if (mr && typeof mr === 'string') return { uuid: '', name: mr };
                const assignor = data.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR');
                return assignor ? { uuid: assignor.person_id || '', name: assignor.name || '' } : { uuid: '', name: '' };
            })(),
            guarantors: (Array.isArray(schema.guarantors) ? schema.guarantors : schema.guarantors?.items || []).map((g: any) => ({
                uuid: g.uuid || g.person_id,
                name: g.name,
                document: g.document,
                entity_type: g.entity_type,
                guarantee_percentage: g.guarantee_percentage || 0,
                participant_type_name: g.participant_type || g.participant_type_name
            })),
            installment: schema.installment || { total: 0, lastDueDate: '', quantity: 0, items: [] },
            fees: schema.fees || { quantity: 0, items: [] }
        },
        base_calculation: schema.base_calculation || {
            tir_annual: null, tir_monthly: null, spread_annual: null,
            vpl: null, vpl_margin: null, duration_days: null
        },
        assignor_billing_address: (schema as any).assignor_billing_address || undefined
    };
};

/**
 * Maps API response to AcknowledgementDebtPage form data.
 */
export const mapAcknowledgementDebtApiResponseToForm = (data: ContractApiResponse, currentForm: AcknowledgementDebtForm) => {
    const schema = data.operation_dictionary || {};
    const assignorPart = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'ASSIGNOR');
    const recipientParticipant = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'RECIPIENT');

    return {
        form: {
            ...currentForm,
            operation_number: data.operation_number || '',
            status: data.status || '',
            sequencial_number: data.sequencial_number || 0,
            assignor: assignorPart?.name || data.assignor || '',
            recipient: recipientParticipant?.name || data.recipient || '',
            originator: data.originator || '',
            with_recourse: data.with_recourse || false,
            operation_uf: schema.operation_uf || '',
            operation_city: schema.operation_city || '',
            credit_product_type_name: data.credit_product_type_name || schema.credit_product_type_name || '',
            receiving_account_name: data.receiving_account_name || schema.receiving_account_name || '',
            total_amount: schema.total_amount || 0,
            nominal_value: data.nominal_value || 0,
            loan_amount: data.loan_amount || 0,
            expiration_date: isoToDateBR(schema.expiration_date),
            emission_date: isoToDateBR(schema.emission_date),
            approved_on: isoToDateBR(schema.approved_on),
            external_link: schema.external_link || '',
            rebate_amount: schema.rebate_amount || 0,
            description_product: schema.description_product || { name: '', id: '' },
            original_contract_discharged: Array.isArray(schema.original_contract_discharged) ? schema.original_contract_discharged : [],
            receivables: mapReceivablesFromSchema(schema),
            disbursement: {
                totalAmount: (schema as any).disbursement?.totalAmount || schema.total_amount || data.nominal_value || 0,
                disbursements: (schema as any).disbursement?.disbursements?.map((d: any) => ({
                    amount: d.amount || 0,
                    date: formatDateWithoutTimezone(d.date)
                })) || []
            },
            installments: (schema as any).installment?.items?.map((inst: any, index: number) => ({
                number: index + 1,
                value: inst.value || inst.amount || 0,
                dueDate: formatDateWithoutTimezone(inst.dueDate),
                format: inst.format || 'VALOR',
                business_reference: inst.business_reference || '',
                document_id: inst.document_id || '',
                document_title: inst.document_title || '',
                receivable_installment: inst.receivable_installment || '',
                debtor_name: inst.debtor_name || '',
                debtor_cpf_cnpj: inst.debtor_cpf_cnpj || '',
            })) || [],
            guarantee_value: schema.guarantee_value || null,
            guarantors: loadGuarantorsFromParticipants(data.participants),
            main_risk: (() => {
                const mr = schema.main_risk as any;
                if (mr && typeof mr === 'object' && mr.uuid) return { uuid: mr.uuid, name: mr.name || '' };
                if (mr && typeof mr === 'string') return { uuid: '', name: mr };
                const assignor = data.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR');
                return assignor ? { uuid: assignor.person_id || '', name: assignor.name || '' } : { uuid: '', name: '' };
            })(),
            fees: loadFees(schema.fees),
            artifacts: schema.artifacts || [],
            signed_contract_file: schema.signed_contract_file || { file_id: '', file_name: '' },
            base_calculation: (schema as any).base_calculation || {
                tir_annual: null, tir_monthly: null, spread_annual: null,
                vpl: null, vpl_margin: null, duration_days: null
            },
            billing_config: (schema as any).billing_config || EMPTY_BILLING_CONFIG,
            assignor_billing_address: (schema as any).assignor_billing_address || undefined
        },
        assignorPart,
        recipientParticipant
    };
};

/**
 * Maps API response to StructuredOperationPage form data.
 */
export const mapStructuredOperationApiResponseToForm = (
    data: ContractApiResponse,
    currentForm: StructuredOperationForm,
    reprocessInstallmentNumbers: (items: any[]) => any[]
) => {
    const schema = data.operation_dictionary || {};
    const assignorPart = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'ASSIGNOR');
    const recipientParticipant = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'RECIPIENT');

    return {
        form: {
            ...currentForm,
            operation_number: data.operation_number || '',
            status: data.status || '',
            sequencial_number: data.sequencial_number || 0,
            assignor: assignorPart?.name || data.assignor || '',
            recipient: recipientParticipant?.name || data.recipient || '',
            originator: data.originator || '',
            with_recourse: data.with_recourse || false,
            operation_uf: schema.operation_uf || '',
            operation_city: schema.operation_city || '',
            credit_product_type_name: data.credit_product_type_name || schema.credit_product_type_name || '',
            receiving_account_name: data.receiving_account_name || schema.receiving_account_name || '',
            total_amount: schema.total_amount || 0,
            nominal_value: data.nominal_value || 0,
            loan_amount: data.loan_amount || 0,
            expiration_date: isoToDateBR(schema.expiration_date),
            emission_date: isoToDateBR(schema.emission_date),
            approved_on: isoToDateBR(schema.approved_on),
            external_link: schema.external_link || '',
            rebate_amount: schema.rebate_amount || 0,
            description_product: schema.description_product || { name: '', id: '' },
            receivables: mapReceivablesFromSchema(schema),
            disbursement: {
                totalAmount: (schema as any).disbursement?.totalAmount || schema.total_amount || data.nominal_value || 0,
                disbursements: (schema as any).disbursement?.disbursements?.map((d: any) => ({
                    amount: d.amount || 0,
                    date: formatDateWithoutTimezone(d.date)
                })) || []
            },
            installments: reprocessInstallmentNumbers(
                (schema as any).installment?.items?.map((inst: any, index: number) => ({
                    number: index + 1,
                    value: inst.value || inst.amount || 0,
                    dueDate: formatDateWithoutTimezone(inst.dueDate),
                    format: inst.format || 'VALOR',
                    business_reference: inst.business_reference || '',
                    document_id: inst.document_id || '',
                    document_title: inst.document_title || '',
                    receivable_installment: inst.receivable_installment || '',
                    debtor_name: inst.debtor_name || '',
                    debtor_cpf_cnpj: inst.debtor_cpf_cnpj || '',
                })) || []
            ),
            guarantee_value: schema.guarantee_value || null,
            guarantors: loadGuarantorsFromParticipants(data.participants),
            main_risk: (() => {
                const mr = schema.main_risk as any;
                if (mr && typeof mr === 'object' && mr.uuid) return { uuid: mr.uuid, name: mr.name || '' };
                if (mr && typeof mr === 'string') return { uuid: '', name: mr };
                return assignorPart ? { uuid: assignorPart.person_id || '', name: assignorPart.name || '' } : { uuid: '', name: '' };
            })(),
            fees: loadFees(schema.fees),
            fiduciary_alienation: (Array.isArray(schema.fiduciary_alienation) ? schema.fiduciary_alienation : []).map((item: any) => ({
                name: item.name || '',
                value: item.value || 0,
                description: item.description || undefined,
                guarantee_type: item.guarantee_type || 'AF'
            })),
            artifacts: schema.artifacts || [],
            signed_contract_file: schema.signed_contract_file || { file_id: '', file_name: '' },
            base_calculation: (schema as any).base_calculation || {
                tir_annual: null, tir_monthly: null, spread_annual: null,
                vpl: null, vpl_margin: null, duration_days: null
            },
            billing_config: (schema as any).billing_config || EMPTY_BILLING_CONFIG,
            assignor_billing_address: (schema as any).assignor_billing_address || undefined
        },
        assignorPart,
        recipientParticipant
    };
};

/**
 * Maps API response to ReceivablesAssignmentPage form data.
 */
export const mapReceivablesAssignmentApiResponseToForm = (data: ContractApiResponse, currentForm: Record<string, any>) => {
    const schema = data.operation_dictionary || {};
    const rawData = data as any;
    const rawSchema = schema as any;
    const assignorParticipant = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'ASSIGNOR');
    const recipientParticipant = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'RECIPIENT');

    return {
        form: {
            ...currentForm,
            type: schema.type || 'RECEIVABLES_ASSIGNMENT',
            operation_number: data.operation_number || '',
            status: data.status || '',
            sequencial_number: data.sequencial_number || 0,
            assignor: assignorParticipant?.name || '',
            recipient: recipientParticipant?.name || '',
            originator: data.originator || '',
            with_recourse: data.with_recourse || false,
            credit_product_type_name: data.credit_product_type_name || schema.credit_product_type_name || '',
            receiving_account_name: data.receiving_account_name || schema.receiving_account_name || '',
            assignment_type_name: rawData.assignment_type || '',
            created_at: formatDateWithoutTimezone(rawData.created_at) || new Date().toLocaleDateString('pt-BR'),
            expiration_date: schema.expiration_date || '',
            emission_date: schema.emission_date || '',
            approved_on: formatDateWithoutTimezone(data.approved_on || schema.approved_on || ''),
            total_amount: schema.total_amount || data.nominal_value || 0,
            loan_amount: data.loan_amount || 0,
            external_link: schema.external_link || '',
            rebate_amount: schema.rebate_amount || 0,
            guarantee_percentage: rawSchema.guarantee_percentage || (assignorParticipant as any)?.guarantee_percentage || 0,
            receivables: mapReceivablesFromSchema(schema),
            disbursement: {
                disbursements: rawSchema.disbursement?.disbursements || [],
                totalAmount: rawSchema.disbursement?.totalAmount || schema.total_amount || data.nominal_value || 0
            },
            base_calculation: schema.base_calculation || {
                tir_annual: null, tir_monthly: null, spread_annual: null,
                vpl: null, vpl_margin: null, duration_days: null
            },
            billing_config: rawSchema.billing_config || EMPTY_BILLING_CONFIG,
            assignor_billing_address: rawSchema.assignor_billing_address || undefined
        },
        assignorParticipant,
        recipientParticipant
    };
};
